import isEmpty from 'lodash/isEmpty'
import { ChangeEvent } from 'react'
import { FiClock, FiGrid, FiPieChart, FiShare2, FiLink } from 'react-icons/fi'
import { RiFileListLine } from 'react-icons/ri'
import { customReportsApi } from '~/api/reports'
import { Msg, useMsg } from '~/common/localizationUtils'
import { ItemsListItem } from '~/components/v2/ItemsList'
import {
  LINKS_AND_BUTTONS_EXCLUDING_THE_TITLE,
  useChangeBgColorOnHover
} from '~/components/v2/ResultsLayout/ResultItem'
import { TitleLockupHeading } from '~/components/v2/TitleLockup'
import { getLocalizedName } from '~/custom_reports/builder/state'
import { REPORTS_HOME_PATH, reportSectionLabel, reportTypeKeys } from '~/custom_reports/constants'
import FavoriteReportCheckbox from '~/custom_reports/home/FavoriteReportCheckbox'
import { Attribute } from '~/custom_reports/home/ReportsPanel'
import { useCondenseRecipients } from '~/custom_reports/hooks/useCondenseRecipients'
import { ShareSettingsType } from '~/custom_reports/types'
import { ReportListItem } from '~/custom_reports/types/api'
import {
  Box,
  ButtonGroup,
  Checkbox,
  Flex,
  Text,
  TitleBlockSubtext,
  TitleBlockTwoLine,
  useDateFormatters
} from '~/design_system'
import { ReportExport } from '~/reports/table/ReportExport'
import { getFlagDetails } from '~/custom_reports/v3/utils'

const getReportQuery = (report: ReportListItem) => async () => {
  const reportApi = customReportsApi.getSavedReport
  const fullReport = await reportApi(report.id)
  return fullReport.query
}

export const replaceReportUrl = (report: { url: string }) =>
  report?.url?.replace(/^\/custom_reports/, REPORTS_HOME_PATH)

export const getShareDetails = (
  shareSettings: ShareSettingsType | null,
  msg: ReturnType<typeof useMsg>,
  condenseRecipients: (recipients: Array<string>, limit?: number) => string,
  limit = 1
) => {
  if (!shareSettings || isEmpty(shareSettings)) return null
  if (!shareSettings.is_shared_by_me) {
    return { text: msg('custom_report.home.shared_by_value', { name: shareSettings.shared_by }) }
  }

  return shareSettings.shared_with
    ? {
        text: msg('custom_report.home.shared_with_value', {
          recipients: condenseRecipients(shareSettings.shared_with, limit)
        }),
        tooltip: shareSettings.shared_with.length > limit && shareSettings.shared_with.join(', ')
      }
    : { text: '', tooltip: '' }
}

interface ReportItemProps {
  report: ReportListItem
  onSelectReport?: ((event: ChangeEvent<HTMLInputElement>) => void) | undefined
  canExport?: boolean
  isSelectable?: boolean
  isSelected?: boolean
  index: number
}

export function ReportItem({ report, onSelectReport, canExport, isSelectable, isSelected, index }: ReportItemProps) {
  const msg = useMsg()
  const dateFmt = useDateFormatters()
  const condenseRecipients = useCondenseRecipients()
  const reportUrl = replaceReportUrl(report)
  const shareDetails = getShareDetails(report?.share_settings, msg, condenseRecipients)
  const datasetName = report.dataset_l10n_name_key
    ? getLocalizedName(report.dataset_l10n_name_key, report.dataset_l10n_name_context, msg)
    : report.dataset
  const sectionName = reportSectionLabel(report.section_name, msg)
  const reportTypeKey = reportTypeKeys[report.report_type as keyof typeof reportTypeKeys]
  const lastRan = report.last_ran ? dateFmt.formatDateTime(report.last_ran) : null

  const isStandardReport = report.report_type === 'standard'

  const reportItemUrl = !isStandardReport ? `${REPORTS_HOME_PATH}/${report.report_type}/${report.id}` : reportUrl

  const hasFlag = getFlagDetails(report.linked_usages)

  const hoverRef = useChangeBgColorOnHover()

  return (
    <ItemsListItem
      ref={hoverRef}
      data-testid={`report-link-btn-${index}`}
      sx={{ [LINKS_AND_BUTTONS_EXCLUDING_THE_TITLE]: { zIndex: 2 } }}>
      <Flex justifyContent="space-between" alignItems="center" width="100%">
        <Flex alignItems="center" maxW="90%">
          <Checkbox
            size="lg"
            mr={2}
            data-testid={`select-report-${index}`}
            onChange={onSelectReport}
            isDisabled={!isSelectable}
            isChecked={isSelected}
            aria-label={msg('common.ui.select_term', { term: msg('common.ui.report') })}
          />

          <FavoriteReportCheckbox
            id={report.id}
            name={report.name}
            reportType={report.report_type}
            isFavorite={report.is_favorite}
          />
          <Box ml={6} maxW="95%">
            <TitleBlockTwoLine mb={2}>
              <TitleLockupHeading href={reportItemUrl} data-testid={`report-title-txt-${index}`}>
                {isEmpty(report.name) ? (
                  <Text as="span" fontStyle="italic">
                    <Msg id="custom_report.builder.new.title" />
                  </Text>
                ) : (
                  report.name
                )}
              </TitleLockupHeading>
              <TitleBlockSubtext textSize="md" isTruncated>
                {report.description}
              </TitleBlockSubtext>
            </TitleBlockTwoLine>
            <Box display={{ base: 'block', md: 'flex' }}>
              {report.report_type !== 'standard' && (
                <Attribute
                  text={msg('custom_report.home.dataset_value', { dataset: datasetName })}
                  icon={FiPieChart}
                  tooltip={msg('custom_report.dataset.label')}
                  data-hoist-above-parent-hover
                  data-testid={`report-dataset-txt-${index}`}
                />
              )}
              <Attribute
                text={sectionName}
                icon={FiGrid}
                tooltip={msg('custom_report.report_section.label')}
                data-hoist-above-parent-hover
                data-testid={`report-section-txt-${index}`}
              />
              <Attribute
                text={reportTypeKey ? msg(reportTypeKey) : report.report_type}
                icon={RiFileListLine}
                tooltip={msg('custom_report.report_type.label')}
                data-hoist-above-parent-hover
                data-testid={`report-type-txt-${index}`}
              />
              {lastRan && (
                <Attribute
                  text={msg('custom_report.home.last_run_value', { timestamp: lastRan })}
                  icon={FiClock}
                  tooltip={msg('custom_report.home.tooltip.report_last_run')}
                  data-hoist-above-parent-hover
                  data-testid={`report-last-run-txt-${index}`}
                />
              )}
              {shareDetails && (
                <Attribute
                  text={shareDetails.text}
                  icon={FiShare2}
                  tooltip={
                    shareDetails.tooltip
                      ? msg('custom_report.home.tooltip.share_details_with_list', { details: shareDetails.tooltip })
                      : msg('custom_report.shared_details')
                  }
                  data-hoist-above-parent-hover
                  data-testid={`report-share-details-txt-${index}`}
                />
              )}
              {hasFlag && (
                <Attribute
                  text={
                    <>
                      <Msg id="custom_report.linked_workflows" />{' '}
                    </>
                  }
                  icon={FiLink}
                  tooltip={msg('custom_report.linked_workflows')}
                  data-hoist-above-parent-hover
                  data-testid={`linked-usage-${index}`}
                />
              )}
            </Box>
          </Box>
        </Flex>
        <Box mt={5}>
          <ButtonGroup display="flex" flexWrap="wrap">
            {canExport && (
              <ReportExport
                queryFinder={getReportQuery(report)}
                {...report}
                queryParams={{ ...report }}
                isStandardReport={isStandardReport}
                data-testid={`report-export-btn-${index}`}
              />
            )}
          </ButtonGroup>
        </Box>
      </Flex>
    </ItemsListItem>
  )
}
